import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Trash2, Sparkles, Heart } from "lucide-react";

/**
 * Compact to-do widget for clients in counselling.
 *
 * Props:
 *  - clientId?: string  — if provided AND therapistMode=true, lets a therapist add a todo for that client
 *  - therapistMode?: bool
 *  - title?: string
 */
export default function TodoList({ clientId, therapistMode = false, title = "Your to-do list" }) {
  const [todos, setTodos] = useState([]);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (therapistMode) return; // therapist view loads from client detail endpoint instead
    try {
      const { data } = await api.get("/todos");
      setTodos(data);
    } catch {
      /* ignore — empty list will render */
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e?.preventDefault();
    if (!content.trim()) return;
    setBusy(true);
    try {
      const body = therapistMode && clientId ? { content, for_client_id: clientId } : { content };
      await api.post("/todos", body);
      setContent("");
      toast.success(therapistMode ? "Added for your client." : "Added.");
      if (!therapistMode) await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add.");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (t) => {
    try {
      const { data } = await api.patch(`/todos/${t.id}`, { completed: !t.completed });
      setTodos((prev) => prev.map((x) => (x.id === t.id ? data : x)));
    } catch {
      /* keep UI stable */
    }
  };

  const remove = async (t) => {
    try {
      await api.delete(`/todos/${t.id}`);
      setTodos((prev) => prev.filter((x) => x.id !== t.id));
    } catch {
      /* keep UI stable */
    }
  };

  const open = todos.filter((t) => !t.completed);
  const done = todos.filter((t) => t.completed);

  return (
    <div className="rounded-3xl border border-mindora-line bg-mindora-panel p-7 shadow-clay" data-testid="todo-list">
      <div className="flex items-center gap-2 text-mindora-red text-xs uppercase tracking-[0.22em]">
        <Sparkles size={14} /> {title}
      </div>
      {therapistMode ? null : (
        <p className="text-xs text-mindora-muted mt-1.5">Things to try this week. Anything tiny counts.</p>
      )}

      <form onSubmit={add} className="mt-5 flex gap-2" data-testid="todo-add-form">
        <Input
          value={content}
          onChange={(e) => setContent(e.target.value)}
          maxLength={300}
          data-testid="todo-input"
          placeholder={therapistMode ? "Add a gentle action item for them…" : "Add something small…"}
          className="bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red"
        />
        <Button type="submit" disabled={busy || !content.trim()} data-testid="todo-add-button" className="btn-primary rounded-full px-5 h-10">
          Add
        </Button>
      </form>

      {!therapistMode && (
        <>
          <ul className="mt-5 space-y-2" data-testid="todo-open-list">
            {open.length === 0 && (
              <li className="text-sm text-mindora-muted italic px-1">Nothing on the list. Maybe rest is the to-do.</li>
            )}
            {open.map((t) => (
              <li key={t.id} className="group flex items-center gap-3 rounded-xl bg-mindora-bg border border-mindora-line px-4 py-3" data-testid={`todo-item-${t.id}`}>
                <button
                  onClick={() => toggle(t)}
                  data-testid={`todo-check-${t.id}`}
                  className="w-5 h-5 rounded-full border border-mindora-line hover:border-mindora-red flex items-center justify-center transition-colors shrink-0"
                  aria-label="Mark complete"
                />
                <div className="flex-1">
                  <div className="text-mindora-ink leading-snug">{t.content}</div>
                  {t.source === "therapist" && (
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-mindora-red mt-1">
                      <Heart size={9} fill="#A73C35" /> from {t.added_by_name || "your therapist"}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => remove(t)}
                  data-testid={`todo-delete-${t.id}`}
                  className="opacity-0 group-hover:opacity-100 text-mindora-muted hover:text-mindora-red transition-all"
                  aria-label="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>

          {done.length > 0 && (
            <div className="mt-6 border-t border-mindora-line pt-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-mindora-muted mb-2">Done · {done.length}</div>
              <ul className="space-y-1.5" data-testid="todo-done-list">
                {done.map((t) => (
                  <li key={t.id} className="flex items-center gap-3 text-sm text-mindora-muted px-1">
                    <button onClick={() => toggle(t)} className="w-5 h-5 rounded-full bg-mindora-red border border-mindora-red text-mindora-bg flex items-center justify-center shrink-0">
                      <Check size={11} />
                    </button>
                    <span className="line-through flex-1">{t.content}</span>
                    <button onClick={() => remove(t)} className="text-mindora-muted hover:text-mindora-red">
                      <Trash2 size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
